"use client";

import React, { useRef } from "react";
import { ErrorMessage } from "@hookform/error-message";
import classNames from "classnames/bind";
import { Controller, RegisterOptions } from "react-hook-form";

import {
  FinancialText,
  FinancialTextProps,
  NumberText,
  NumberTextProps,
} from "@fsg/gui-bits";

import { VALIDATIONS } from "@app/constants";
import { renderErrorMessage, usePlaceholder } from "@app/utils";

import { useFormContext } from "./Form";
import moduleStyles from "./Form.module.scss";
import { FormItem } from "./FormItem";
import { ReadOnlyField } from "./ReadOnlyField";
import dynamic from "next/dynamic";
const ReactQuill = dynamic(() => import("react-quill"), { ssr: false });
//import ReactQuill from 'react-quill'
import "react-quill/dist/quill.snow.css";
import "./RichTextField.scss";
import { cn } from "@fsg/utils";

const cx = classNames.bind(moduleStyles);

/**
 * Checks if the HTML content is empty or just contains empty tags
 * @param content HTML content to check
 * @returns true if content has actual text, false if empty or just contains empty tags
 */
const hasActualContent = (content: string): boolean => {
  if (!content) return false;

  // Check for common empty editor patterns
  if (content === "<p><br></p>" || content === "<p></p>") return false;

  // Create a temporary div to parse the HTML
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = content;

  // Get the text content without HTML tags
  const textContent = tempDiv.textContent || tempDiv.innerText || "";

  // Return true if there is actual text content after trimming whitespace
  return textContent.trim().length > 0;
};

const inputClasses = {
  base: "text-sm font-regular block break-words",
  editMode: "bg-white w-full",
};

type displayOptions = NumberTextProps | FinancialTextProps;

interface TextFieldProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  displayOptions?: displayOptions;
  rules?: Omit<
    RegisterOptions<any, string>,
    "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
  >;
  formItemProps?: React.ComponentProps<typeof FormItem>;
  subtext?: React.ReactNode;
  readOnly?: boolean;
  characterLim?: number;
}

export function RichTextField({
  rules,
  label,
  formItemProps,
  subtext,
  readOnly = false,
  characterLim,
  ...props
}: TextFieldProps) {
  const {
    isEditing,
    control,
    formState: { errors },
  } = useFormContext();

  const ref = useRef(null);

  if (!control) {
    throw new Error("TextField must be used within a Form component");
  }

  const Display = ({ value }: { value: string }) => {
    const displayClassName = cx("m--form-item--input", inputClasses.base);
    const defaultPlaceholder = usePlaceholder(props.placeholder);
    if (
      value === undefined ||
      value === null ||
      value === "" ||
      !hasActualContent(value)
    ) {
      // console.debug('TextField >> ', {field: props.name, value})
      return (
        <span
          className={cx(displayClassName, "text-gray-dark", "html-display")}
        >
          {defaultPlaceholder}
        </span>
      );
    }
    try {
      value = String(value); //REVIEW -> stringify value to avoid erroring with gui-bits, but preserving any legacy implementation

      return (
        <div
          className={cn(
            displayClassName,
            "text-gray-900",
            "rounded-xs",
            "bg-gray-100",
            "border-gray-100",
            {
              "p-md": value !== null || isEditing,
            }
          )}
          dangerouslySetInnerHTML={{ __html: value }}
        ></div>
      );
    } catch (e) {
      console.error(e);
      return <span className="text-error">Error</span>;
    }
  };

  const formItemPropsWithOverrides = {
    label: rules?.required && isEditing ? VALIDATIONS.styleLabel(label) : label,
    ...formItemProps,
  };

  const Quill = ReactQuill as any;
  const toolbarOptions = [
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
  ];

  return (
    <Controller
      name={props.name}
      control={control}
      rules={{ ...rules, maxLength: characterLim }}
      render={({ field }) => {
        const charCount = field.value ? field.value.length : 0; //thuuuu

        return (
          <div className="w-full">
            {readOnly ? (
              <ReadOnlyField label={formItemPropsWithOverrides.label}>
                <Display value={field.value} />
              </ReadOnlyField>
            ) : (
              <FormItem
                {...formItemPropsWithOverrides}
                fullWidthLabel={isEditing || hasActualContent(field.value)}
                className={cx("rich-text-form-item", {
                  "flex-col":
                    (field.value !== null && hasActualContent(field.value)) ||
                    isEditing,
                  flex: field.value === null || !hasActualContent(field.value),
                })}
                padding={"pb-xs"}
              >
                {isEditing ? (
                  <div
                    className={cx(
                      "m--textarea-wrapper",
                      "p-0 border border-border rounded-xs"
                    )}
                  >
                    <Quill
                      modules={{ toolbar: toolbarOptions }}
                      className={"quill-editor"}
                      ref={ref}
                      value={field.value || ""}
                      onKeyDown={(e: any) => {
                        if (
                          ref?.current?.getEditorContents().length >=
                            characterLim &&
                          e.key !== "Backspace"
                        ) {
                          e.preventDefault();
                        }
                      }}
                      onChange={(content: string) => {
                        // Only update with content if it actually has text
                        if (hasActualContent(content)) {
                          field.onChange(content);
                        } else {
                          // If empty, set to empty string instead of empty HTML tags
                          field.onChange("");
                        }
                      }}
                    />
                    {characterLim && (
                      <div
                        className={cx("m--textarea-character-counter")}
                      >{`${charCount}/${characterLim}`}</div>
                    )}
                  </div>
                ) : (
                  <div>
                    <Display value={field.value} />
                  </div>
                )}
              </FormItem>
            )}
            <ErrorMessage
              errors={errors}
              name={field.name}
              render={renderErrorMessage}
            />
            {subtext}
          </div>
        );
      }}
    />
  );
}
